import { Component, DestroyRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { combineLatest, Observable, ReplaySubject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import { NavigationCategory, NavigationService } from '../../services/data-services/navigation/navigation.service';
import { NavigationDataService } from '../../services/data-services/navigation/navigation-data.service';
import { NavigationDataModel } from '../../models/navigation-data.model';
import { UserPreferencesPortfolioService } from 'app/shared/services/data-services/user-preferences-portfolio/user-preferences-portfolio.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NOTIFICATIONS_CONFIG, openSuccess } from '../../helper/notification-message.helper';
import { TranslateService } from '@ngx-translate/core';
import { KeepAliveService } from 'app/shared/services-api-v2/api-v2-keep-alive/keep-alive.service';
import { NavigationLevel } from 'app/shared/services/data-services/navigation/navigation.interface';
import { removeAccentsAndDiacritics } from 'app/shared/helper/common.helper';
import { NoUnderscorePipe } from 'app/shared/pipes/no-underscore/no-underscore.pipe';
import { UserPortfolioPreferencesService } from 'app/shared/services/user-preferences/user-portfolio-preferences.service';
import { IHeaderResponsivenessOptions } from 'app/shared/models/header-display-options';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { ThemeService } from '@wind-log_library/fe-component-library';

@Component({
  selector: 'app-navigation-component',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
    standalone: false
})
export class NavigationComponent implements OnInit, OnDestroy {
	
  @Input() config = NavigationComponentConfig.defaultConfig;
  @Input() headerResponsivenessOptions: IHeaderResponsivenessOptions;

  private isNewEvent = false;
  public isDevMode = /localhost/.test(document.location.host);
  public parkTurbineDropdownData: NavigationDataModel;
  public powerPlantLabels: string[];
  private unsubscriber = new ReplaySubject();
  private portfolioDataSubscription = new Subscription();

  public selectedLevel: NavigationLevel = 'portfolio';
  public moduleName: string;
  public selectedPortfolio = null;
  public selectedPowerplant: any;
  public selectedTurbine: any;
  public filteredItem: any;
  public plantsListWithTurbines = [];
  public powerPlants: any = [];
  public searchAsset = '';
  public parkSearch = '';
  public filteredPlantsTurbines;
  public shown = {
    portfolio: false,
    park: false,
    turbine: false,
    filterPortfolio: false,
    filterPark: false,
  };
  public newPortfolioCreated: boolean = false;
  private lastNavDetails = null;

  private destroyRef = inject(DestroyRef);
  public theme$: Observable<string>;
  public notificationsConfig: any;
  public signalType: string;
  public statusSelected: string;
  public inputsShowOptions;
  public defaultPortfolio = null;

  constructor(
    private navigationService: NavigationService,
    private navigationDataService: NavigationDataService,
    private userPortfolioService: UserPreferencesPortfolioService,
    private keepAliveService: KeepAliveService,
    private noUnderscorePipe: NoUnderscorePipe,
    private translateService: TranslateService,
    private snackBar: MatSnackBar,
    private userPrefencesService: UserPortfolioPreferencesService,
    private themeService: ThemeService
  ) {}

  public get hidePortfolio() {
    return !this.navigationService.isNavigationLevelActivated('portfolio', this.moduleName);
  }

  public get hidePowerplant() {
    return !this.navigationService.isNavigationLevelActivated('park', this.moduleName);
  }

  public get hideTurbine() {
    return !this.navigationService.isNavigationLevelActivated('turbine', this.moduleName);
  }

  public get hideNavigation() {
    if (this.isNewEvent) {
      return true;
    } else {
      return ['preference'].includes(this.moduleName);
    }
  }

  public get isLoading() {
    return false; // this.loadingService.isLoading(this.moduleName);
  }

  public ngOnInit(): void {
    this.navigationService.getIsInNewPlanEvent().subscribe(value => {
      this.isNewEvent = value;
    });
    
    /* Function to keep API V2 connection active and not closing and re-create the socket connection every 50 seconds */
    this.keepAliveService
      .startKeepAlive()
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(d => {});

    this.notificationsConfig = Object.assign({}, NOTIFICATIONS_CONFIG);
    // this.loadingService.isLoading('monitor');
    this.theme$ = this.themeService.getTheme();
    this.subscribeOnPortfolioData();

    this.userPortfolioService.portfolioListUpdate
      .pipe(
        filter(v => !!v),
        takeUntil(this.unsubscriber),
      )
      .subscribe(() => {
        this.cancelPortfolioDataSubscription();
        setTimeout(() => {
          // Give backend some time to process the change, before we request the new list of portfolios:
          this.navigationDataService.updateNavigationData(null).subscribe(data => {
            if (!data) {
              return;
            }
            this.subscribeOnPortfolioData();
          });
        }, 500);
      });

    this.navigationService
      .getNavigationObservable([
        NavigationCategory.Module,
        NavigationCategory.Level,
        NavigationCategory.PortfolioLevelSelectedPortfolio,
        NavigationCategory.PortfolioLevelSelectedParks,
        NavigationCategory.ParkLevelSelectedPark,
        NavigationCategory.ParkLevelSelectedTurbines,
        NavigationCategory.TurbineLevelSelectedTurbine,
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(navDetails => {
        if (JSON.stringify(this.lastNavDetails) === JSON.stringify(navDetails)) {
          return;
        }
        this.lastNavDetails = cloneDeep(navDetails);
        if (this.moduleName !== navDetails.module || this.selectedLevel !== navDetails.level) {
          this.config.setDefault();
        }
        this.moduleName = navDetails.module;
        this.selectedLevel = navDetails.level;
        switch (navDetails.level) {
          case 'portfolio': {
            if (navDetails.selectedPortfolioId) {
              // navDetails.selectedPortfolioId = navDetails.selectedPortfolioId || this.parkTurbineDropdownData.selectedPortfolio.id;
              this.selectedPortfolio = this.parkTurbineDropdownData?.portfolios?.find(
                p => p.id === navDetails?.selectedPortfolioId,
              );
              this.setFilteredPortfolio();
              this.selectedPowerplant = this.getDefaultPowerplant(this.selectedPortfolio, navDetails?.selectedParkId);
              this.selectedTurbine =
                this.selectedPowerplant?.turbines?.find(p => p.id == navDetails?.selectedTurbineId) ||
                this.selectedPowerplant?.turbines?.[0];
              // keep selection if we stay within the same portfolio
              if (
                navDetails.selectedParksIds !== null &&
                (!navDetails.selectedParksIds.every(v => this.filteredItem.powerplants.map(p => p.id).includes(v)) ||
                  navDetails.selectedParksIds.length !== this.filteredItem.powerplants.length)
              ) {
                setTimeout(() => {
                  this.navigationService.setSelectedParksList(this.config.selected, true);
                }, 0);
              }
              this.filterTurbines();
            }
            break;
          }
          case 'park': {
            if (this.parkTurbineDropdownData) {
              // navDetails.selectedParkId = navDetails.selectedParkId || this.parkTurbineDropdownData.selectedPortfolio.powerplants[0].id;
              this.selectedPowerplant = this.parkTurbineDropdownData.powerplants.find(
                p => p.id === navDetails.selectedParkId,
              );
              const firstPortfolio = this.selectedPowerplant
                ? this.parkTurbineDropdownData.portfolios.find(p =>
                    p.powerplants.map(plants => plants.id).includes(this.selectedPowerplant.id),
                  )
                : this.parkTurbineDropdownData.selectedPortfolio.powerplants[0].id;
              this.selectedPortfolio = navDetails.selectedPortfolioId
                ? this.parkTurbineDropdownData.portfolios.find(p => p.id === navDetails.selectedPortfolioId)
                : this.userPrefencesService.getPortfolioId()
                  ? this.parkTurbineDropdownData.portfolios.find(
                      p => p.id === this.userPrefencesService.getPortfolioId(),
                    )
                  : firstPortfolio;

              if (this.selectedPowerplant) {
                if (!this.config.selectedId || this.config.selectedId !== this.selectedPowerplant.id) {
                  this.config.selectedId = this.selectedPowerplant.id;
                  this.config.selected = this.selectedPowerplant.turbines.map(inv => inv.id);
                  setTimeout(() => {
                    this.navigationService.setSelectedTurbinesList(this.selectedPowerplant.turbines.map(inv => inv.id));
                  }, 0);
                } else {
                  this.navigationService.setSelectedTurbinesList(this.config.selected);
                }
                this.filteredItem = cloneDeep(this.selectedPowerplant);
                const powerplant = this.powerPlants.find(
                  plant => this.selectedPowerplant && plant.id === this.selectedPowerplant.id,
                );
                this.selectedTurbine = powerplant.turbines[0];
              } else {
                const powerplant = this.parkTurbineDropdownData.selectedPortfolio.powerplants[0];
                setTimeout(() => {
                  this.navigationService.redirectToPowerplant(
                    powerplant.id,
                    powerplant.turbines.map(turbine => turbine.id),
                  );
                }, 0);
              }
            }
            break;
          }
          case 'turbine': {
            if (this.parkTurbineDropdownData) {
              const firstPortfolio = this.parkTurbineDropdownData.portfolios.find(p =>
                p.powerplants.map(plants => plants.id).includes(this.selectedPowerplant.id),
              );
              this.selectedPortfolio = navDetails.selectedPortfolioId
                ? this.parkTurbineDropdownData.portfolios.find(p => p.id === navDetails.selectedPortfolioId)
                : this.userPrefencesService.getPortfolioId()
                  ? this.parkTurbineDropdownData.portfolios.find(
                      p => p.id === this.userPrefencesService.getPortfolioId(),
                    )
                  : firstPortfolio;
              // navDetails.selectedTurbineId = navDetails.selectedTurbineId || this.parkTurbineDropdownData.selectedPortfolio.powerplants[0].turbines[0].id;
              this.selectedTurbine = this.plantsListWithTurbines.find(p => p.id === navDetails.selectedTurbineId);
              if (this.selectedTurbine) {
                this.selectedPowerplant = this.powerPlants.find(plant =>
                  plant.turbines.find(turbine => turbine.id === this.selectedTurbine.id),
                );
              } else {
                setTimeout(() => {
                  this.navigationService.redirectToTurbine(
                    this.parkTurbineDropdownData.selectedPortfolio.powerplants[0].turbines[0].id,
                  );
                }, 0);
              }
            }
            break;
          }
        }
      });
  }

  public selectPortfolio(e) {
    if (this.isLoading) return;
    if (
      this.shown.portfolio ||
      this.shown.park ||
      this.shown.turbine ||
      this.shown.filterPortfolio ||
      this.isFiltered('portfolio')
    )
      return;
    if (this.selectedLevel !== 'portfolio') {
      this.config.setDefault();
    }
    const isButton = (e.target as Element).classList.contains('fas') || (e.target as Element).classList.contains('btn');
    if (!isButton) {
      this.navigationService.redirectToPortfolio(this.selectedPortfolio.id, [], this.selectedPortfolio.name);
    }
  }

  public selectPowerplant(e) {
    if (this.isLoading) return;
    if (this.shown.portfolio || this.shown.park || this.shown.turbine || this.shown.filterPark) return;
    if (this.selectedLevel !== 'park') {
      this.config.setDefault();
    }
    const isButton = (e.target as Element).classList.contains('fas') || (e.target as Element).classList.contains('btn');
    if (!isButton) {
      this.navigationService.redirectToPowerplant(
        this.selectedPowerplant.id,
        this.selectedPowerplant.turbines.map(turbine => turbine.id),
        this.selectedPowerplant.name,
      );
    }
  }

  public selectTurbine(e) {
    if (this.isLoading) return;
    if (this.shown.portfolio || this.shown.park || this.shown.turbine) return;
    const isButton = (e.target as Element).classList.contains('fas') || (e.target as Element).classList.contains('btn');
    
    if (!isButton) {
      this.config.setDefault();
      this.navigationService.redirectToTurbine(this.selectedTurbine.id, this.selectedTurbine.code);
    }
  }

  public navigateToPowerplant(powerplantId: string): void {
    const powerplant = this.powerPlants.find(plant => plant.id === powerplantId);
    if (this.isParkDeactivated(powerplantId, powerplant.energyMeterAvailable)) {
      return;
    }

    this.shown.park = false;
    this.config.setDefault();

    this.selectedTurbine = powerplant.turbines[0];
    this.selectedPowerplant = powerplant;

    this.navigationService.redirectToPowerplant(
      powerplantId,
      powerplant.turbines.map(turbine => turbine.id),
      powerplant.name,
    );
  }

  public navigateToTurbine(turbineId: string): void {
    if (this.isTurbineDeactivated(turbineId)) {
      return;
    }
    this.shown.turbine = false;
    this.config.setDefault();

    this.selectedPowerplant = this.powerPlants.find(plant => plant.turbines.find(turbine => turbine.id === turbineId));
    this.selectedTurbine = this.selectedPowerplant.turbines.find(turbine => turbine.id === turbineId);
    this.navigationService.redirectToTurbine(turbineId, this.selectedTurbine.code);
  }

  public navigateToPortfolio(portfolio): void {
    this.shown.park = false;
    this.shown.portfolio = false;
    this.shown.filterPortfolio = false;
    this.config.setDefault();

    this.selectedPortfolio = portfolio;
    this.filterTurbines();
    this.setFilteredPortfolio();
    this.selectedPowerplant = this.getDefaultPowerplant(portfolio);
    this.selectedTurbine = this.selectedPowerplant?.turbines[0];
    this.navigationService.redirectToPortfolio(
      portfolio?.id,
      this.filteredItem.powerplants.map(p => p.id),
      portfolio?.name,
    );
  }

  public ngOnDestroy(): void {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  public onShown(item) {
    if (this.isLoading) return;
    this.shown[item] = true;
    setTimeout(() => {
      const selected = document.querySelector('.active-element');
      if (selected) {
        selected.scrollIntoView();
      }
    }, 1);
    this.hidePopovers(item);
  }

  public onShownFilter(item) {
    this.shown[item] = true;
    this.hidePopovers(item);
  }

  public onHidden(item) {
    this.shown[item] = false;
    if (item === 'park') {
      this.parkSearch = '';
    }
  }

  public unpin() {
    this.shown.filterPortfolio = this.selectedLevel === 'portfolio';
    this.shown.filterPark = this.selectedLevel === 'park';
  }

  public hidePopovers(newOpen) {
    for (const popoverName in this.shown) {
      if (popoverName !== newOpen) {
        this.shown[popoverName] = false;
      }
    }
  }

  private cancelPortfolioDataSubscription() {
    this.portfolioDataSubscription.unsubscribe();
  }

  public createNewPortfolio(parks) {
    const portfolio = {
      name: this.config.name,
      defaultPortfolio: false,
      parks,
    };
    combineLatest([this.userPortfolioService.createCustomPorfolio({ portfolio })])
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(([res]) => {
        this.cancelPortfolioDataSubscription();
        this.navigationDataService.updateNavigationData(res.data.createUserCustomPortfolio._id).subscribe(data => {
          if (!data) {
            return;
          }
          this.showSuccessMessage('HEADER.PORTFOLIO_CREATED');

          this.subscribeOnPortfolioData();
          this.newPortfolioCreated = true;
          this.shown.filterPortfolio = false;
          this.config.setDefault();
          this.parkTurbineDropdownData = data;
          this.generateListWithPowerPlantsAndTurbines(data.powerplants);
          this.selectedPortfolio = this.parkTurbineDropdownData?.portfolios.find(
            p => p.id === res?.data?.createCustomPortfolio?._id,
          );
          this.filterTurbines();
          this.updatePortfolioList(data);
          this.navigateToPortfolio(this.selectedPortfolio);
      })
    })
  }

  private showSuccessMessage(msgKey) {
    const message = this.translateService.instant(msgKey);
    openSuccess(this.snackBar, message);
  }

  public isFiltered(level) {
    const items = level === 'portfolio' ? this.selectedPortfolio?.powerplants : this.selectedPowerplant?.turbines;
    return this.config?.selected.length !== items?.length && this.selectedLevel === level;
  }

  public pinToggle() {
    this.config.sticky = !this.config.sticky;
  }

  public isParkDeactivated(parkId, energyMeterAvailable = false) {
    return (
      (!energyMeterAvailable && (this.moduleName === 'check_revenues' || this.moduleName === 'revenues')) ||
      this.navigationService.isParkDeactivated(parkId)
    );
  }

  public isTurbineDeactivated(turbineId) {
    return this.navigationService.isTurbineDeactivated(turbineId);
  }

  public filterTurbines(data = this.selectedPortfolio) {
    data.powerplants.forEach(plant => {
      plant.turbines.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' }));
    });
    this.filteredPlantsTurbines = cloneDeep(data)
      ?.powerplants.map(plant => {
        const searchName = removeAccentsAndDiacritics(this.searchAsset.trim().toLowerCase());
        const plantName = removeAccentsAndDiacritics(plant.displayName.toLowerCase());
        if (!plantName.includes(searchName)) {
          plant.turbines = plant.turbines?.filter(inv => {
            const invName = this.noUnderscorePipe.transform(inv.code.toLowerCase());
            return removeAccentsAndDiacritics(invName).includes(searchName);
          });
        }
        return plant;
      })
      .filter(plant => plant.turbines?.length);
  }

  public changePlantSelection() {
    this.navigationService.setSelectedParksList(this.config.selected, true);
  }

  public changeTurbineSelection() {
    this.navigationService.setSelectedTurbinesList(this.config.selected);
  }

  public showSelectTurbinesButton() {
    return this.navigationService.showSelectTurbinesButton();
  }

  public changeStatus({ option, index }) {
    this.statusSelected = option;
    this.signalType = this.inputsShowOptions.values[index];
  }

  public filterParks(search: string) {
    this.parkSearch = search.trim().toLowerCase();
  }

  public showFilteredPark(name) {
    const processedName = removeAccentsAndDiacritics(name.toLowerCase());
    const processedSearch = removeAccentsAndDiacritics(this.parkSearch);
    return processedName.includes(processedSearch);
  }

  private getDefaultPowerplant(portfolio, preferredParkId?: string) {
    const powerplants = portfolio?.powerplants ?? [];
    const isRevenuesModule = this.moduleName === 'check_revenues' || this.moduleName === 'revenues';
    const isSelectable = p => !isRevenuesModule || !this.isParkDeactivated(p.id, p.energyMeterAvailable);

    return (
      powerplants.find(p => p.id === preferredParkId && isSelectable(p)) ||
      (isRevenuesModule ? powerplants.find(isSelectable) : null) ||
      powerplants[0]
    );
  }

  private setFilteredPortfolio() {
    if (!this.config.selectedId || this.config.selectedId !== this.selectedPortfolio.id) {
      this.config.selectedId = this.selectedPortfolio.id;
      this.config.selected = this.selectedPortfolio.powerplants
        .filter(powerplant => !this.isParkDeactivated(powerplant.id, powerplant.energyMeterAvailable))
        .map(plant => plant.id);
    }
    this.filteredItem = cloneDeep(this.selectedPortfolio);
  }

  private subscribeOnPortfolioData(): void {
    this.portfolioDataSubscription = this.navigationDataService
      .loadNavigationData()
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(data => {
        if (!data) {
          return;
        }
        if (!this.newPortfolioCreated) {
          this.parkTurbineDropdownData = data;
          this.defaultPortfolio = cloneDeep(this.parkTurbineDropdownData.portfolios.find(p => p.defaultPortfolio));
          if (!this.shown.turbine) {
            const { options, active } = data.navigationFilterOptions;
            this.signalType = active.value;
            this.config.setOptions(options, active);
            this.inputsShowOptions = {
              names: this.config.options.map(option => `HEADER.${option.label.toUpperCase().replace(' ', '_')}`),
              values: this.config.options.map(option => option.value),
            };
            const index = this.inputsShowOptions.values.indexOf(this.signalType) || 0;
            this.statusSelected = this.inputsShowOptions.names[index];
          }
          this.generateListWithPowerPlantsAndTurbines(data.powerplants);
          this.updatePortfolioList(data);
        } else {
          this.newPortfolioCreated = false;
        }
      });
  }

  private updatePortfolioList(data) {
    if (!this.selectedPortfolio) {
      this.selectedPortfolio = data.selectedPortfolio;
      this.filterTurbines();
      this.setFilteredPortfolio();
      this.navigationService.setSelectedParksList(
        this.filteredItem.powerplants.map(plant => plant.id),
        false,
      );
      this.selectedPowerplant = this.getDefaultPowerplant(data.selectedPortfolio);
      this.selectedTurbine = this.selectedPowerplant ? this.selectedPowerplant.turbines[0] : null;
    } else {
      this.filterTurbines(data.selectedPortfolio);
      this.selectedPortfolio.powerplants.forEach(powerplant => {
        const mainPortfolio = data.portfolios.find(portfolio => portfolio.id === '0');
        if (mainPortfolio) {
          const plantData = mainPortfolio.powerplants.find(plant => plant.id === powerplant.id);
          if (plantData) {
            powerplant.state = plantData.state;
            powerplant.turbines = plantData.turbines;
          }
        }
      });
    }
    this.powerPlantLabels = data.powerplants.map(p => p.name);
  }

  private generateListWithPowerPlantsAndTurbines(powerPlants: any[]): void {
    this.plantsListWithTurbines = [];
    this.powerPlants = powerPlants;
    powerPlants.forEach(p => {
      this.plantsListWithTurbines = [...this.plantsListWithTurbines, ...p.turbines].sort((a, b) =>
        a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' }),
      );
    });
  }

  public goToNextTurbine(isForward) {
    const park = this.selectedPortfolio.powerplants.find(plant => plant.id === this.selectedPowerplant.id);
    const turbines = this.filteredTurbines(park.turbines);
    const indexOfTurbine = turbines.findIndex(turbine => turbine.id === this.selectedTurbine.id);
    const nextTurbineIndex = isForward ? indexOfTurbine + 1 : indexOfTurbine - 1;
    if (nextTurbineIndex < 0 || nextTurbineIndex >= park.turbines.length) {
      return;
    }
    this.navigateToTurbine(turbines[nextTurbineIndex].id);
  }

  public get hasNextTurbine() {
    const park = this.selectedPortfolio.powerplants.find(plant => plant.id === this.selectedPowerplant.id);
    const turbines = this.filteredTurbines(park.turbines);
    const indexOfTurbine = turbines.findIndex(turbine => turbine.id === this.selectedTurbine.id);
    return indexOfTurbine < turbines.length - 1;
  }

  public get hasPrevTurbine() {
    return this.selectedTurbineIndex > 0;
  }

  public get selectedTurbineIndex() {
    const park = this.selectedPortfolio.powerplants.find(plant => plant.id === this.selectedPowerplant.id);
    const turbines = this.filteredTurbines(park.turbines);
    return turbines.findIndex(turbine => turbine.id === this.selectedTurbine.id);
  }

  public filteredTurbines(turbines) {
    return turbines.filter(turbine => !this.isTurbineDeactivated(turbine.id));
  }
  
  public checkEllipsis(element: HTMLElement, tooltip: MatTooltip): void {
    tooltip.disabled = element.scrollWidth <= element.clientWidth;
  }

}


export class NavigationComponentConfig {

  public static defaultConfig = new NavigationComponentConfig(false, []);

  // set this flag to true if you want the overlays to be sticky-able
  public sticky: boolean;
  public selected: string[];
  public selectedId: string;
  public name?: string;

  // which signal you want to see in the overlay per plant/turbine; so far only 'status' is supported,
  // later also 'efficiency', 'performance_ratio' etc. could be supported
  public signalType: string;
  public options: any[];

  public constructor(sticky, selected = [], name = '') {
    this.sticky = sticky;
    this.selected = selected;
    this.name = name;
  }

  public setOptions(options, active) {
    this.options = [...options];
    this.signalType = active.value;
  }

  public setDefault() {
    this.sticky = false;
    this.selectedId = '';
    this.name = '';
    this.selected = [];
  }
}
